/**
 * Internal helper: pad a number with a leading zero.
 */
const pad = (num: number): string => num.toString().padStart(2, "0");

const THRESHOLD_SECONDS_TO_MILLIS = 1_000_000_000_000;

// ─── Synchronized Time Offset ──────────────────────────────────────────────────

let timeOffset = 0;
let cachedClientIp = "";
let syncPromise: Promise<void> | null = null;

/**
 * Initializes the synchronized time offset and client IP by fetching from the API once.
 * Subsequent calls reuse the in-flight or completed promise.
 */
export const initSynchronizedTime = (): Promise<void> => {
    syncPromise ??= (async (): Promise<void> => {
        try {
            const localBefore = Date.now();
            const response = await fetch("/api/time");
            const contentType = response.headers.get("content-type");
            if (response.ok && contentType?.includes("application/json")) {
                const data = (await response.json()) as {
                    clientIp?: string;
                    date_time?: string;
                    dateTime?: string;
                };
                if (data?.clientIp) {
                    cachedClientIp = data.clientIp;
                }
                const timeString = data.date_time || data.dateTime;
                if (typeof timeString === "string") {
                    const apiTime = Date.parse(timeString);
                    if (!Number.isNaN(apiTime)) {
                        const localAfter = Date.now();
                        const localMid = Math.round((localBefore + localAfter) / 2);
                        timeOffset = apiTime - localMid;
                    }
                }
            }
        } catch (error) {
            /* eslint-disable no-console */
            console.error("Failed to fetch time from API, using client machine time:", error);
            /* eslint-enable no-console */
        }
    })();

    return syncPromise;
};

/**
 * Gets the client IP address captured during synchronized time initialization.
 */
export const getClientIp = (): string => cachedClientIp;

/**
 * Gets the current unix timestamp in milliseconds, synchronized with America/Denver time zone via API.
 * Falls back to Date.now() if the API call fails or returns an invalid response.
 */
export const getNow = (): number => {
    return Date.now() + timeOffset;
};

export const accuracyCheck = (): string => {
    const today = new Date(getNow());
    const y = today.getFullYear();
    const mm = today.getMonth() + 1;
    const dd = today.getDate();
    const hh = today.getHours();
    const ii = today.getMinutes();
    const ss = today.getSeconds();

    return `${y}-${mm.toString().padStart(2, "0")}-${dd
        .toString()
        .padStart(2, "0")} ${hh.toString().padStart(2, "0")}:${ii
        .toString()
        .padStart(2, "0")}:${ss.toString().padStart(2, "0")}`;
};

// ─── Validation ───────────────────────────────────────────────────────────────

const sanitizeDateString = (s: string): string => s.replaceAll(/[^0-9T: /-]/g, "");

export const isValidDate = (dateString: string): boolean => {
    const regEx = /^\d{4}-\d{2}-\d{2}$/;
    if (!regEx.exec(dateString)) return false;
    const d = new Date(dateString);
    const dNum = d.getTime();
    if (!dNum && dNum !== 0) return false;
    return d.toISOString().slice(0, 10) === dateString;
};

// ─── Epoch helpers ────────────────────────────────────────────────────────────

/**
 * Converts a date string to epoch milliseconds.
 * Returns null when dateString is falsy.
 */
export const getEpoch = (dateString: string | null | undefined): number | null => {
    if (!dateString) return null;
    const safe = sanitizeDateString(dateString);
    if (!/^\d{4}[/\- ]\d{2}[/\- ]\d{2}/.test(safe)) return null;
    const normalized = safe.replaceAll("T", " ").replaceAll("-", "/");
    const epoch = Date.parse(normalized);
    return Number.isNaN(epoch) ? null : epoch;
};

/**
 * Converts an epoch (seconds or milliseconds) to a formatted string.
 * Automatically detects seconds vs milliseconds.
 * @returns YYYY-MM-DD@HH:MM:SS
 */
export const getDateFromEpoch = (epoch: number | string): string => {
    if (!epoch) return "";
    let numericEpoch = Number(epoch);
    if (Number.isNaN(numericEpoch)) return "";
    if (numericEpoch < THRESHOLD_SECONDS_TO_MILLIS) {
        numericEpoch *= 1000;
    }
    const d = new Date(numericEpoch);
    if (Number.isNaN(d.getTime())) return "";
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}@${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

// ─── Date formatters ──────────────────────────────────────────────────────────

/**
 * Formats an ISO date string to "Month D, YYYY" (optionally abbreviated month, optionally with time).
 */
export const formatDate = (dateString: string | null = null, abbr = false, includeTime = false): string | null => {
    if (!dateString) return dateString;

    const myDate = new Date(dateString.replaceAll("-", "/").replaceAll("T", " "));
    const Y = myDate.getFullYear();
    const month = myDate.toLocaleString("default", { month: abbr ? "short" : "long" });
    const dd = myDate.getDate();

    if (includeTime) {
        let hh = myDate.getHours();
        const ii = pad(myDate.getMinutes());
        let meridian = "am";
        if (hh >= 12) {
            hh -= 12;
            meridian = "pm";
        }
        if (hh === 0) {
            hh = 12;
        }
        return `${month} ${dd}, ${Y} at ${hh}:${ii} ${meridian}`;
    }

    return `${month} ${dd}, ${Y}`;
};

/**
 * Converts a date string to "YYYY-MM-DD 00:00:00" format.
 */
export const formatIsoDate = (dateString: string | null = null): string | null => {
    if (!dateString) return dateString;
    const myDate = new Date(dateString.replaceAll("-", "/").replaceAll("T", " "));
    const Y = myDate.getFullYear();
    const m = pad(myDate.getMonth() + 1);
    const d = pad(myDate.getDate());
    return `${Y}-${m}-${d} 00:00:00`;
};

/**
 * Returns today's date as "MM-DD".
 */
export const getToday = (): string => {
    const d = new Date(getNow());
    return `${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * Returns the current year as a string.
 */
export const getSingleYear = (): string => String(new Date(getNow()).getFullYear());

/**
 * Returns today's date as "YYYY-MM-DD".
 */
export const getTodayWithYear = (): string => {
    const d = new Date(getNow());
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * Returns the current date+time as "YYYY-MM-DD HH:MM:SS".
 */
export const getTodayWithTime = (): string => {
    const d = new Date(getNow());
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

/**
 * Converts a date string to "Month D, YYYY" format.
 */
export const getEnglishDate = (dateString: string | null = null): string | null => {
    if (!dateString) return dateString;
    const myDate = new Date(dateString.replaceAll("-", "/").replaceAll("T", " "));
    if (Number.isNaN(myDate.getTime())) return dateString;
    const Y = myDate.getFullYear();
    const month = myDate.toLocaleString("default", { month: "long" });
    const dd = myDate.getDate();
    let hh = myDate.getHours();
    const ii = pad(myDate.getMinutes());
    let meridian = "am";
    if (hh >= 12) {
        meridian = "pm";
    }
    if (hh > 12) {
        hh -= 12;
    }
    if (hh === 0) {
        hh = 12;
    }
    return `${month} ${dd}, ${Y} at ${hh}:${ii} ${meridian}`;
};

export const getCountdownDays = (dateString: string | null = null): string | null => {
    if (!dateString) return null;
    const myDate = new Date(dateString.replaceAll("-", "/").replaceAll("T", " "));
    if (Number.isNaN(myDate.getTime())) return null;
    const today = new Date(getNow());
    const diffMs = myDate.getTime() - today.getTime();
    const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return `${Math.max(0, days)}`;
};
